<template>
  <app-page class="subconto-analysis-page" labelledby="page-title" full-width>
    <page-header
      title="Анализ субконто"
      description="Сальдо и обороты по выбранной аналитике сразу по всем счетам."
    >
      <template #status>
        <q-badge outline :color="headerStatus.color">{{ headerStatus.label }}</q-badge>
      </template>
      <template v-if="reportData && reportData.organization.name" #metadata>
          {{ reportData.organization.name }}
          <span v-if="reportData.organization.inn">, ИНН {{ reportData.organization.inn }}</span>
      </template>
      <template #actions>
        <q-btn
          flat
          no-caps
          color="primary"
          icon="arrow_back"
          label="К аналитике"
          :to="{ name: 'analytics_trial_balance' }"
        />
      </template>
    </page-header>

    <q-btn
      class="subconto-mobile-filter-trigger"
      outline
      no-caps
      color="primary"
      icon="filter_alt"
      label="Параметры отчета"
      data-testid="subconto-filters-trigger"
      @click="openFilters"
    />
    <button
      v-if="filtersOpen"
      class="subconto-filter-backdrop"
      type="button"
      aria-label="Закрыть параметры отчета"
      @click="closeFilters"
    />

    <div class="subconto-workspace" data-testid="subconto-workspace">
      <aside
        class="subconto-master-panel"
        :class="{ 'subconto-master-panel--open': filtersOpen }"
        :aria-hidden="$q.screen.lt.sm && !filtersOpen"
        :aria-modal="$q.screen.lt.sm && filtersOpen ? 'true' : undefined"
        :role="$q.screen.lt.sm ? 'dialog' : 'complementary'"
        aria-labelledby="subconto-filter-title"
        data-testid="subconto-master-panel"
      >
        <div class="row items-start q-col-gutter-sm">
          <div class="col">
            <div id="subconto-filter-title" class="text-subtitle1 text-weight-medium">Параметры отчета</div>
            <div class="text-caption text-grey-7">Период, субконто, группировка и отбор</div>
          </div>
          <div class="col-auto">
            <q-badge outline color="primary">{{ selectedPresetLabel || 'Без пресета' }}</q-badge>
            <q-btn
              class="subconto-mobile-filter-close"
              dense
              flat
              round
              icon="close"
              aria-label="Закрыть параметры отчета"
              @click="closeFilters"
            />
          </div>
        </div>

        <q-separator class="q-my-sm" />

        <div class="subconto-section">
          <div class="subconto-section-title">Период</div>
          <div class="subconto-period-grid">
            <q-input dense outlined label="С начала" v-model="filters.start" :mask="dateInputMask" @update:model-value="markStale">
              <template v-slot:append>
                <q-icon name="event" class="cursor-pointer" role="button" tabindex="0" aria-label="Выбрать дату начала" @keydown.enter.prevent="$event.currentTarget.click()" @keydown.space.prevent="$event.currentTarget.click()">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-date v-model="filters.start" :mask="quasarDateMask" @update:model-value="markStale" />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
            <q-input dense outlined label="По дату" v-model="filters.end" :mask="dateInputMask" @update:model-value="markStale">
              <template v-slot:append>
                <q-icon name="event" class="cursor-pointer" role="button" tabindex="0" aria-label="Выбрать дату окончания" @keydown.enter.prevent="$event.currentTarget.click()" @keydown.space.prevent="$event.currentTarget.click()">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-date v-model="filters.end" :mask="quasarDateMask" @update:model-value="markStale" />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <q-select
            dense
            outlined
            emit-value
            map-options
            class="q-mt-sm"
            label="Пресет"
            v-model="selectedPreset"
            :options="presetOptions"
            @update:model-value="applyPreset"
          />
        </div>

        <div class="subconto-section">
          <div class="subconto-section-title">Субконто</div>
          <q-select
            dense
            outlined
            multiple
            use-chips
            emit-value
            map-options
            label="Виды субконто"
            v-model="filters.subconto_types"
            :options="subcontoOptions"
            @update:model-value="onSubcontoTypesChange"
          />
        </div>

        <div class="subconto-section">
          <div class="subconto-section-title">Группировка</div>
          <q-option-group
            dense
            inline
            type="checkbox"
            v-model="filters.grouping"
            :options="groupingOptions"
            @update:model-value="markStale"
          />
          <q-select
            dense
            outlined
            emit-value
            map-options
            class="q-mt-sm"
            label="Периодичность"
            v-model="filters.periodicity"
            :options="periodicityOptions"
            @update:model-value="markStale"
          />
        </div>

        <div class="subconto-section">
          <div class="subconto-section-title">Отбор</div>
          <q-select
            dense
            outlined
            emit-value
            map-options
            label="Субконто"
            v-model="filterDraft.subconto_type"
            :options="subcontoOptions"
            @update:model-value="loadFilterValues"
          />
          <q-select
            dense
            outlined
            multiple
            use-input
            use-chips
            emit-value
            map-options
            class="q-mt-sm"
            label="Значения"
            v-model="filterDraft.values"
            :options="filterValueOptions"
            :loading="filterLoading"
            @filter="filterValueSearch"
          />
          <div class="row q-col-gutter-sm q-mt-sm">
            <div class="col">
              <q-btn dense outline color="primary" icon="filter_alt" label="Применить" class="full-width" @click="applyValueFilter" />
            </div>
            <div class="col">
              <q-btn dense flat color="primary" icon="clear" label="Сбросить" class="full-width" @click="clearValueFilters" />
            </div>
          </div>
          <div v-if="filters.filters.length" class="subconto-active-filters q-mt-sm">
            <q-chip
              v-for="item in filters.filters"
              :key="`${item.subconto_type}-${item.values.join(',')}`"
              dense
              color="primary"
              text-color="white"
            >
              {{ typeLabel(item.subconto_type) }}: {{ item.values.length }}
              <q-btn
                class="subconto-filter-remove"
                dense
                flat
                round
                color="white"
                icon="close"
                :aria-label="`Удалить фильтр «${typeLabel(item.subconto_type)}»`"
                @click.stop="removeValueFilter(item.subconto_type)"
              />
            </q-chip>
          </div>
        </div>

        <div class="subconto-section">
          <div class="subconto-section-title">Показатели</div>
          <div class="subconto-toggle-grid">
            <q-toggle dense v-model="filters.expanded_balance" label="Развернутое сальдо" @update:model-value="markStale" />
            <q-toggle dense v-model="filters.include_subaccounts" label="По субсчетам" @update:model-value="markStale" />
            <q-toggle dense v-model="filters.include_quantity" label="Количество" @update:model-value="markStale" />
            <q-toggle dense v-model="filters.include_zero" label="Нулевые строки" @update:model-value="markStale" />
            <q-toggle dense v-model="debitCreditOnly" label="Только Дт и Кт сальдо" />
          </div>
        </div>

        <div class="subconto-section">
          <q-input dense outlined v-model="tableSearch" label="Поиск по таблице">
            <template v-slot:prepend>
              <q-icon name="search" aria-hidden="true" />
            </template>
            <template v-if="tableSearch" #append>
              <q-btn flat round dense icon="close" aria-label="Очистить поиск по таблице" @click="tableSearch = ''" />
            </template>
          </q-input>
        </div>

        <div class="subconto-action-row">
          <q-btn no-caps color="primary" icon="play_arrow" label="Сформировать" :loading="loading" @click="generateReport" />
          <q-btn no-caps flat color="primary" icon="restart_alt" label="Сбросить параметры" :disable="loading" @click="resetReportFilters" />
          <report-artifact-actions
            :payload="artifactPayload"
            :org-id="connection.org_id"
            :disabled="!hasResult || stale || loading"
            stacked
          />
          <q-btn no-caps outline color="primary" icon="download" label="CSV" :disable="!hasResult || stale" @click="downloadCsv" />
        </div>
      </aside>

      <section class="subconto-result-panel" aria-label="Результат анализа субконто" data-testid="subconto-result-panel">
        <q-banner v-if="stale" dense rounded class="bg-warning text-dark">
          Фильтры изменены. Нажмите “Сформировать”, чтобы обновить отчет.
        </q-banner>
        <app-state
          v-if="loading && !hasResult"
          state="loading"
          title="Формируем анализ субконто"
          message="Собираем обороты, документы и проводки."
        />
        <app-state
          v-else-if="state === 'error' && !hasResult"
          state="error"
          title="Не удалось сформировать отчет"
          :message="errorMessage"
          @retry="generateReport"
        />
        <app-state
          v-else-if="state === 'idle'"
          state="empty"
          title="Отчет еще не сформирован"
          message="Настройте период, аналитику и нажмите «Сформировать»."
        />
        <app-state
          v-else-if="hasResult && !tableRows.length"
          state="filtered-empty"
          title="По заданным параметрам данных нет"
          message="Измените период, субконто или отбор и сформируйте отчет повторно."
          @reset="resetReportFilters"
        />

        <template v-else-if="hasResult">
        <q-linear-progress v-if="loading" indeterminate color="primary" class="q-mb-sm" />

        <div class="subconto-result-summary">
          <div>
            <div class="text-caption text-grey-7">Виды субконто</div>
            <div class="text-body2">{{ activeSubcontoLabels }}</div>
          </div>
          <div>
            <div class="text-caption text-grey-7">Группировка</div>
            <div class="text-body2">{{ activeGroupingLabels }}</div>
          </div>
          <div>
            <div class="text-caption text-grey-7">Отборы</div>
            <div class="text-body2">{{ filters.filters.length || 0 }}</div>
          </div>
        </div>

        <div v-accessible-scroll-region="'Результат анализа субконто'" class="subconto-table-wrap q-mt-sm" data-testid="subconto-table-wrap">
          <q-table
            flat
            bordered
            dense
            separator="cell"
            row-key="id"
            :rows="tableRows"
            :columns="tableColumns"
            :pagination="tablePagination"
            :loading="loading"
            loading-label="Формирование отчета"
            no-data-label="Данные отчета не сформированы"
            @row-click="selectRow"
          >
            <template v-slot:body="props">
              <q-tr :props="props" :class="rowClass(props.row)" @click="selectRow($event, props.row)">
                <q-td key="label" :props="props" class="subconto-sticky-col">
                  <div class="row items-center no-wrap" :style="{ paddingLeft: `${props.row.depth * 18}px` }">
                    <q-btn
                      v-if="hasChildren(props.row)"
                      dense
                      flat
                      round
                      size="sm"
                      :icon="isExpanded(props.row) ? 'expand_more' : 'chevron_right'"
                      @click.stop="toggleRow(props.row)"
                    />
                    <div v-else class="subconto-spacer" />
                    <span class="ellipsis">{{ props.row.label }}</span>
                  </div>
                </q-td>
                <q-td
                  v-for="column in numericColumns"
                  :key="column.name"
                  :props="props"
                  :class="amountClass(props.row[column.name])"
                >
                  {{ numericValue(props.row[column.name], column) }}
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </div>

        <q-expansion-item
          class="subconto-diagnostics q-mt-md"
          bordered
          dense
          icon="fact_check"
          :label="`Диагностика · ${diagnostics.length}`"
          :caption="diagnostics.length ? 'Проверьте сообщения диагностики' : 'Ошибок и предупреждений нет'"
        >
          <q-list v-if="diagnostics.length" dense separator>
            <q-item v-for="item in diagnostics" :key="`${item.code}-${item.message}`">
              <q-item-section avatar><q-icon name="warning" color="warning" /></q-item-section>
              <q-item-section>
                <q-item-label>{{ item.message }}</q-item-label>
                <q-item-label v-if="item.code" caption>{{ item.code }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
          <div v-else class="q-pa-md text-body2 text-grey-7">Диагностических сообщений нет.</div>
        </q-expansion-item>
        </template>
      </section>
    </div>

    <q-drawer v-model="drawerOpen" side="right" overlay bordered :width="430">
      <div class="q-pa-md">
        <div class="row items-start q-gutter-sm no-wrap">
          <div class="col">
            <div class="text-h6">{{ selectedRow ? selectedRow.label : 'Детализация' }}</div>
            <div v-if="selectedRow" class="text-caption text-grey-7">{{ selectedRow.model_type }}</div>
          </div>
          <q-btn dense flat round icon="close" @click="drawerOpen = false" />
        </div>

        <q-list dense bordered class="q-mt-md">
          <q-item v-for="column in numericColumns" :key="column.name">
            <q-item-section>{{ column.label }}</q-item-section>
            <q-item-section side>{{ numericValue(selectedRow && selectedRow[column.name], column) }}</q-item-section>
          </q-item>
        </q-list>

        <div class="text-subtitle2 q-mt-md">Проводки</div>
        <q-list dense bordered class="q-mt-sm">
          <q-item v-for="transfer in selectedTransfers" :key="transfer.id || transfer.transfer_id">
            <q-item-section>
              <q-item-label>{{ transfer.label || transfer.name || `Проводка ${transfer.transfer_id || transfer.id}` }}</q-item-label>
              <q-item-label caption>
                {{ transfer.date || '' }}
                <span v-if="transfer.amount !== undefined && transfer.amount !== null && transfer.amount !== ''"> · {{ formatMoney(transfer.amount) }}</span>
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item v-if="!selectedTransfers.length">
            <q-item-section>
              <q-item-label caption>Проводки доступны после раскрытия до нижнего уровня.</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <div class="text-subtitle2 q-mt-md">Документы</div>
        <q-list dense bordered class="q-mt-sm">
          <q-item v-for="document in selectedDocuments" :key="document.document_id || document.id" clickable @click="openDocument(document)">
            <q-item-section>
              <q-item-label>{{ document.label || document.name || `Документ ${document.document_id || document.id}` }}</q-item-label>
              <q-item-label caption>{{ document.document_type || '' }}</q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-icon name="open_in_new" />
            </q-item-section>
          </q-item>
          <q-item v-if="!selectedDocuments.length">
            <q-item-section>
              <q-item-label caption>Связанные документы не найдены.</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </div>
    </q-drawer>
  </app-page>
</template>

<script>
import moment from 'moment'
import { formatMoney } from '../utils/reportFormatters'
import AppPage from '../../../../components/ui/AppPage.vue'
import AppState from '../../../../components/ui/AppState.vue'
import PageHeader from '../../../../components/ui/PageHeader.vue'
import {
  DATE_INPUT_MASK,
  QUASAR_DATE_MASK,
  normalizeDate as normalizeDisplayDate
} from '../../../../services/dateFormat'
import {
  buildReportArtifactPayload,
  getSubcontoAnalysisReport,
  getSubcontoMetadata,
  searchSubcontoValues
} from '../api/reportsApi'
import { normalizeSubcontoAnalysisReport } from '../utils/reportNormalizer'
import ReportArtifactActions from '../../../../components/documents/ReportArtifactActions.vue'

const defaultColumns = [
  { name: 'label', label: 'Субконто / счет', field: 'label', align: 'left' },
  { name: 'debit_start', label: 'Сальдо нач. Дт', field: 'debit_start', align: 'right' },
  { name: 'credit_start', label: 'Сальдо нач. Кт', field: 'credit_start', align: 'right' },
  { name: 'debit_period', label: 'Оборот Дт', field: 'debit_period', align: 'right' },
  { name: 'credit_period', label: 'Оборот Кт', field: 'credit_period', align: 'right' },
  { name: 'debit_end', label: 'Сальдо кон. Дт', field: 'debit_end', align: 'right' },
  { name: 'credit_end', label: 'Сальдо кон. Кт', field: 'credit_end', align: 'right' }
]

const documentRoutes = {
  upd: 'document_info_upd',
  sale: 'document_info_upd',
  purchase: 'document_info_upd',
  payment: 'document_info_payment',
  avance: 'document_info_factura_avance',
  factura_avance: 'document_info_factura_avance',
  accept_factura_avance: 'document_info_factura_avance',
  bill_to_pay: 'document_info_bill_to_pay',
  act_sver: 'document_info_act_sver'
}

export default {
  name: 'SubcontoAnalysisPage',
  components: { AppPage, AppState, PageHeader, ReportArtifactActions },
  data() {
    const storedPeriod = this.$store.state.filter_period || {}
    const query = this.$route.query || {}
    return {
      metadata: null,
      selectedPreset: query.preset || 'mutual_settlements',
      filters: {
        start: this.normalizeDate(query.start || storedPeriod.from || moment().startOf('quarter')),
        end: this.normalizeDate(query.end || storedPeriod.to || moment().endOf('quarter')),
        subconto_types: ['counterparty', 'contract', 'settlement_document'],
        grouping: ['document', 'transfer'],
        include_subaccounts: true,
        expanded_balance: true,
        periodicity: 'none',
        include_quantity: false,
        include_zero: false,
        filters: []
      },
      filterDraft: {
        subconto_type: 'counterparty',
        values: []
      },
      filterValueOptions: [],
      filterLoading: false,
      reportData: null,
      state: 'idle',
      loading: false,
      stale: false,
      errorMessage: '',
      expandedRows: {},
      tableSearch: '',
      debitCreditOnly: false,
      drawerOpen: false,
      filtersOpen: false,
      selectedRow: null,
      tablePagination: {
        rowsPerPage: 50
      },
      dateInputMask: DATE_INPUT_MASK,
      quasarDateMask: QUASAR_DATE_MASK
    }
  },
  computed: {
    hasResult() {
      return this.reportData !== null
    },
    presetOptions() {
      return ((this.metadata && this.metadata.presets) || []).map((item) => ({
        label: item.label,
        value: item.code
      }))
    },
    selectedPresetLabel() {
      const item = this.presetOptions.find((option) => option.value === this.selectedPreset)
      return item ? item.label : ''
    },
    subcontoOptions() {
      return ((this.metadata && this.metadata.subconto_types) || []).map((item) => ({
        label: item.label,
        value: item.code
      }))
    },
    activeSubcontoLabels() {
      const labels = this.filters.subconto_types.map((type) => this.typeLabel(type)).filter(Boolean)
      return labels.length ? labels.join(', ') : 'Не выбрано'
    },
    groupingOptions() {
      return [
        { label: 'Документы', value: 'document' },
        { label: 'Проводки', value: 'transfer' }
      ]
    },
    activeGroupingLabels() {
      const labels = this.filters.grouping.map((value) => {
        const option = this.groupingOptions.find((item) => item.value === value)
        return option ? option.label : value
      })
      return labels.length ? labels.join(', ') : 'Без детализации'
    },
    periodicityOptions() {
      return [
        { label: 'Без периодов', value: 'none' },
        { label: 'По дням', value: 'day' },
        { label: 'По месяцам', value: 'month' }
      ]
    },
    rawColumns() {
      if (this.reportData && this.reportData.columns && this.reportData.columns.length) {
        return this.reportData.columns
      }
      if (this.metadata && this.metadata.columns && this.metadata.columns.length) {
        return this.metadata.columns
      }
      return defaultColumns
    },
    tableColumns() {
      return this.rawColumns
        .filter((column) => this.filters.include_quantity || !column.quantity)
        .map((column) => ({
          name: column.name,
          label: column.label,
          field: column.name,
          align: column.align || (column.name === 'label' ? 'left' : 'right'),
          sortable: column.name !== 'label',
          quantity: Boolean(column.quantity)
        }))
    },
    numericColumns() {
      return this.tableColumns.filter((column) => column.name !== 'label')
    },
    tableRows() {
      const rows = this.reportData ? this.flattenRows(this.reportData.rows, false) : []
      const text = String(this.tableSearch || '').trim().toLowerCase()
      return rows.filter((row) => {
        if (text && !String(row.label || '').toLowerCase().includes(text)) {
          return false
        }
        if (this.debitCreditOnly && !this.hasDebitAndCreditSaldo(row)) {
          return false
        }
        return true
      })
    },
    diagnostics() {
      return this.reportData ? this.reportData.diagnostics : []
    },
    selectedTransfers() {
      return this.selectedRow ? this.collectRows(this.selectedRow, 'transfer') : []
    },
    selectedDocuments() {
      return this.selectedRow ? this.collectRows(this.selectedRow, 'document') : []
    },
    artifactPayload() {
      return buildReportArtifactPayload(this.connection, 'subconto_analysis', this.filters)
    },
    headerStatus() {
      if (this.loading) return { color: 'info', label: 'Формируется' }
      if (this.state === 'error') return { color: 'negative', label: 'Ошибка' }
      if (this.stale) return { color: 'warning', label: 'Требует обновления' }
      if (this.hasResult) return { color: 'positive', label: 'Сформирован' }
      return { color: 'grey-7', label: 'Не сформирован' }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
    this.syncQuery()
    this.loadMetadata()
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    normalizeDate(value) {
      return normalizeDisplayDate(value)
    },
    async loadMetadata() {
      try {
        this.metadata = await getSubcontoMetadata(this.connection, this.emitter)
        this.applyPreset(this.selectedPreset)
      } catch (error) {
        this.errorMessage = error.message || 'Не удалось загрузить настройки отчета'
        this.state = 'error'
      }
    },
    applyPreset(code) {
      const preset = ((this.metadata && this.metadata.presets) || []).find((item) => item.code === code)
      if (!preset) {
        return
      }
      this.filters.subconto_types = preset.subconto_types.slice(0, 3)
      this.filters.include_subaccounts = preset.include_subaccounts !== false
      this.filters.expanded_balance = preset.expanded_balance !== false
      this.filters.include_quantity = Boolean(preset.include_quantity)
      this.markStale()
    },
    onSubcontoTypesChange(value) {
      if (value.length > 3) {
        this.filters.subconto_types = value.slice(0, 3)
        this.$q.notify({ type: 'warning', message: 'В отчете можно выбрать не более трех видов субконто' })
      }
      this.markStale()
    },
    markStale() {
      if (this.hasResult) {
        this.stale = true
      }
      this.$store.commit('updateFilterPeriod', {
        from: this.filters.start,
        to: this.filters.end
      })
      this.syncQuery()
    },
    async loadFilterValues() {
      this.filterDraft.values = []
      this.filterValueOptions = []
      await this.searchFilterValues('')
    },
    filterValueSearch(value, update) {
      this.searchFilterValues(value).then(() => {
        update(() => {})
      })
    },
    async searchFilterValues(query) {
      if (!this.filterDraft.subconto_type) {
        return
      }
      this.filterLoading = true
      try {
        const response = await searchSubcontoValues(this.connection, this.emitter, this.filterDraft.subconto_type, query, 30)
        this.filterValueOptions = (response.values || []).map((item) => ({
          label: item.value_label,
          value: item.value_key
        }))
      } catch (error) {
        this.$q.notify({ type: 'negative', message: error.message || 'Не удалось найти значения субконто' })
      } finally {
        this.filterLoading = false
      }
    },
    applyValueFilter() {
      if (!this.filterDraft.subconto_type || !this.filterDraft.values.length) {
        return
      }
      this.filters.filters = [
        ...this.filters.filters.filter((item) => item.subconto_type !== this.filterDraft.subconto_type),
        {
          subconto_type: this.filterDraft.subconto_type,
          values: this.filterDraft.values.slice()
        }
      ]
      this.markStale()
    },
    removeValueFilter(type) {
      this.filters.filters = this.filters.filters.filter((item) => item.subconto_type !== type)
      this.markStale()
    },
    clearValueFilters() {
      this.filters.filters = []
      this.filterDraft.values = []
      this.markStale()
    },
    typeLabel(type) {
      const item = ((this.metadata && this.metadata.subconto_types) || []).find((option) => option.code === type)
      return item ? item.label : type
    },
    async generateReport() {
      this.closeFilters()
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await getSubcontoAnalysisReport(this.connection, this.emitter, this.filters)
        this.reportData = normalizeSubcontoAnalysisReport(response)
        this.expandedRows = {}
        this.expandTopRows(this.reportData.rows)
        this.state = 'success'
        this.stale = false
      } catch (error) {
        this.errorMessage = error.message || 'Не удалось сформировать отчет'
        this.state = 'error'
      } finally {
        this.loading = false
      }
    },
    expandTopRows(rows) {
      const rootRows = rows || []
      rootRows.forEach((row) => {
        this.expandedRows[row.id] = true
        const children = row.children || []
        children.forEach((child) => {
          this.expandedRows[child.id] = true
        })
      })
    },
    hasChildren(row) {
      return row && row.children && row.children.length > 0
    },
    isExpanded(row) {
      return Boolean(this.expandedRows[row.id])
    },
    toggleRow(row) {
      this.expandedRows = {
        ...this.expandedRows,
        [row.id]: !this.expandedRows[row.id]
      }
    },
    flattenRows(rows, includeCollapsed) {
      const result = []
      const visit = (items) => {
        const list = items || []
        list.forEach((item) => {
          result.push(item)
          if ((includeCollapsed || this.isExpanded(item)) && item.children && item.children.length) {
            visit(item.children)
          }
        })
      }
      visit(rows)
      return result
    },
    collectRows(row, modelType) {
      const result = []
      const visit = (item) => {
        if (!item) {
          return
        }
        if (item.model_type === modelType) {
          result.push(item)
        }
        const children = item.children || []
        children.forEach(visit)
      }
      visit(row)
      return result
    },
    selectRow(event, row) {
      this.selectedRow = row
      this.drawerOpen = true
    },
    hasDebitAndCreditSaldo(row) {
      const start = this.toNumber(row.debit_start) > 0 && this.toNumber(row.credit_start) > 0
      const end = this.toNumber(row.debit_end) > 0 && this.toNumber(row.credit_end) > 0
      return start || end
    },
    toNumber(value) {
      return Number(String(value || '0').replace(/\s/g, '').replace(',', '.')) || 0
    },
    formatMoney,
    numericValue(value, column) {
      const amount = value === undefined || value === null || value === '' ? 0 : value
      return column.quantity ? String(amount) : formatMoney(amount)
    },
    amountClass(value) {
      return this.toNumber(value) === 0 ? 'text-grey-6 text-right' : 'text-weight-medium text-right'
    },
    rowClass(row) {
      return {
        'bg-grey-1': row.model_type === 'subconto',
        'text-weight-bold': row.depth === 0 || row.model_type === 'account'
      }
    },
    csvEscape(value) {
      const text = String(value === undefined || value === null ? '' : value)
      return `"${text.replace(/"/g, '""')}"`
    },
    downloadCsv() {
      const rows = this.reportData ? this.flattenRows(this.reportData.rows, true) : []
      const header = this.tableColumns.map((column) => this.csvEscape(column.label)).join(';')
      const body = rows.map((row) => {
        return this.tableColumns.map((column) => this.csvEscape(row[column.name] || '')).join(';')
      }).join('\n')
      const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `subconto-analysis-${this.filters.start}-${this.filters.end}.csv`
      link.click()
      URL.revokeObjectURL(url)
    },
    openDocument(document) {
      const id = document.document_id || document.id
      const routeName = documentRoutes[document.document_type] || 'document_info_upd'
      if (!id) {
        return
      }
      this.$router.push({ name: routeName, params: { id, history_id: 'last' } })
    },
    syncQuery() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          start: this.filters.start,
          end: this.filters.end,
          preset: this.selectedPreset
        }
      }).catch(() => {})
    },
    resetReportFilters() {
      this.selectedPreset = 'mutual_settlements'
      this.filters = {
        start: moment().startOf('quarter').format('DD.MM.YYYY'),
        end: moment().endOf('quarter').format('DD.MM.YYYY'),
        subconto_types: ['counterparty', 'contract', 'settlement_document'],
        grouping: ['document', 'transfer'],
        include_subaccounts: true,
        expanded_balance: true,
        periodicity: 'none',
        include_quantity: false,
        include_zero: false,
        filters: []
      }
      this.filterDraft = { subconto_type: 'counterparty', values: [] }
      this.tableSearch = ''
      this.debitCreditOnly = false
      this.reportData = null
      this.state = 'idle'
      this.stale = false
      this.applyPreset(this.selectedPreset)
      this.syncQuery()
    },
    openFilters() {
      this.filtersOpen = true
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const closeButton = this.$el.querySelector('.subconto-mobile-filter-close')
          if (closeButton) closeButton.focus()
        })
      })
    },
    closeFilters() {
      this.filtersOpen = false
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.filtersOpen) {
        this.closeFilters()
      }
    }
  }
}
</script>

<style>
.subconto-analysis-page {
  max-width: 100%;
  overflow-x: hidden;
}

.subconto-analysis-page .subconto-workspace {
  display: grid;
  grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
  gap: var(--sid-space-4);
  align-items: start;
}

.subconto-analysis-page .subconto-master-panel,
.subconto-analysis-page .subconto-table-wrap,
.subconto-analysis-page .subconto-result-summary {
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-large);
  background: var(--sid-color-surface-0);
}

.subconto-analysis-page .subconto-master-panel {
  position: sticky;
  top: var(--sid-space-3);
  padding: var(--sid-space-4);
}

.subconto-analysis-page .subconto-result-panel {
  min-width: 0;
}

.subconto-analysis-page .subconto-section {
  padding: var(--sid-space-3) 0;
  border-bottom: 1px solid var(--sid-color-line);
}

.subconto-analysis-page .subconto-section-title {
  margin-bottom: 8px;
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
  font-weight: var(--sid-font-weight-semibold);
  text-transform: uppercase;
}

.subconto-analysis-page .subconto-period-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sid-space-2);
}

.subconto-analysis-page .subconto-toggle-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--sid-space-1);
}

.subconto-analysis-page .subconto-action-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--sid-space-2);
  padding-top: var(--sid-space-3);
}

.subconto-analysis-page .subconto-active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sid-space-1);
}

.subconto-analysis-page .subconto-result-summary {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr) minmax(80px, 0.4fr);
  gap: var(--sid-space-3);
  padding: var(--sid-space-3);
}

.subconto-analysis-page .subconto-table-wrap {
  overflow-x: auto;
  max-width: 100%;
}

.subconto-analysis-page .subconto-table-wrap table {
  min-width: 980px;
}

.subconto-analysis-page .q-table thead th:first-child,
.subconto-analysis-page .subconto-sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: inherit;
}

.subconto-analysis-page .q-table thead th:first-child {
  z-index: 3;
  background: var(--sid-color-surface-0);
}

.subconto-analysis-page .subconto-spacer {
  width: 28px;
  min-width: 28px;
}

@media (max-width: 1100px) {
  .subconto-analysis-page .subconto-workspace {
    grid-template-columns: minmax(0, 1fr);
  }

  .subconto-analysis-page .subconto-master-panel {
    position: static;
  }

  .subconto-analysis-page .subconto-action-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.subconto-mobile-filter-trigger,
.subconto-mobile-filter-close,
.subconto-filter-backdrop {
  display: none;
}

@media (max-width: 640px) {
  .subconto-mobile-filter-trigger {
    display: inline-flex;
    width: 100%;
    min-height: var(--sid-touch-target);
    margin-bottom: var(--sid-space-4);
  }

  .subconto-mobile-filter-close {
    display: inline-flex;
  }

  .subconto-filter-backdrop {
    position: fixed;
    z-index: 3999;
    inset: 0;
    display: block;
    width: 100%;
    border: 0;
    background: var(--sid-color-overlay);
  }

  .subconto-analysis-page .subconto-master-panel {
    position: fixed;
    z-index: 4000;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(24rem, 92vw);
    overflow-y: auto;
    border: 0;
    border-left: 1px solid var(--sid-color-line);
    border-radius: 0;
    visibility: hidden;
    transform: translateX(100%);
    transition: transform 160ms ease, visibility 160ms;
  }

  .subconto-analysis-page .subconto-master-panel--open {
    visibility: visible;
    transform: translateX(0);
  }

  .subconto-analysis-page .subconto-period-grid,
  .subconto-analysis-page .subconto-result-summary,
  .subconto-analysis-page .subconto-action-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
